import React, { useCallback, useEffect, useMemo, useState } from 'react';
import avo from 'analytics/avo';
import PropTypes from 'prop-types';

import useCart from 'data-hooks/useCart';
import useMultiItemRecommendations from 'hooks/useMultiItemRecommendations';
import RecommendationProduct from '../RecommendationProduct';

const CartRecommendations = ({ closeCart }) => {
  const {
    data: { cart, addingToCart },
    actions: { addToCart },
  } = useCart();
  const {
    getMultiRecommendedProducts,
    data: recommendedData,
  } = useMultiItemRecommendations();

  const [recommendedProductsList, setRecommendedProductsList] = useState([]);
  const [addingToCartId, setAddingToCartId] = useState(null);

  const handleAddToCart = async (
    productId,
    selectedOptions = {},
    productDetails = {}
  ) => {
    setAddingToCartId(productId);
    const { error, cartToken } = await addToCart({
      productId,
      selectedOptions: !selectedOptions ? {} : selectedOptions,
      decideLater: false,
      quantity: 1,
      preventHideCart: true,
    });
    if (!error && productDetails?.name) {
      setAddingToCartId(null);
      avo.productAdded({
        productId: +productId,
        nonInteraction: 1,
        price: parseFloat(productDetails?.price),
        fullPrice: parseFloat(productDetails?.fullPrice),
        quantity: 1,
        sku: productDetails?.sku,
        cartId: cartToken,
        name: productDetails?.name,
        variant: productDetails?.variant,
        quickship: false,
        location: 'Cart',
        component: 'Cart',
        fabricName: productDetails?.fabricName,
        fabricColor: productDetails?.fabricColor,
        currency: 'USD',
      });
    }
  };

  const productsData = useMemo(() => {
    if (cart?.length) {
      const perItemLimit = Math.ceil(10 / cart.length);
      const updatedProductsData = cart.map(item => ({
        id: item.product_id,
        limit: perItemLimit,
      }));
      return updatedProductsData;
    }
    return null;
  }, [cart]);

  useEffect(() => {
    if (!productsData?.length) return;
    getMultiRecommendedProducts({ variables: { products: productsData } });
  }, [productsData, getMultiRecommendedProducts]);

  useEffect(() => {
    if (!recommendedData || !cart?.length) return;
    const recommendedList = recommendedData.getMultiItemToItemRecommendation;
    let updatedList = [];
    const productIdsInCart = cart.map(item => item.product_id);
    const idsInList = [];
    cart.forEach(item => {
      const cartList = (recommendedList?.[item.product_id] || [])
        .map(pId => {
          if (productIdsInCart.includes(pId.id) || idsInList.includes(pId.id)) {
            return null;
          }
          idsInList.push(pId.id);
          return {
            id: pId.id,
            cartOptions: item.options,
            cartSku: item.sku,
            cartPickLater: item.pickLater,
          };
        })
        .filter(Boolean);
      updatedList = [...new Set([...updatedList, ...cartList])];
    });
    setRecommendedProductsList(updatedList.slice(0, 10));
  }, [recommendedData, cart]);

  const removeProductIdFromList = useCallback(
    id => {
      setRecommendedProductsList(prevList =>
        prevList.filter(item => item.id !== id)
      );
    },
    [setRecommendedProductsList]
  );

  if (!recommendedProductsList?.length) return <div />;

  return (
    <div className="CartRecommendations text-gray mt-0 mb-8">
      <div className="CartRecommendations__header mt-6 text-gray text-base font-bold">
        Recommendations
      </div>
      <div className="items-start flex flex-wrap flex-row">
        {recommendedProductsList.map(product => (
          <RecommendationProduct
            key={product.id}
            product={product}
            addToCart={handleAddToCart}
            addingToCart={addingToCart && addingToCartId === product.id}
            closeCart={closeCart}
            addingToCartId={addingToCartId}
            removeProductIdFromList={removeProductIdFromList}
          />
        ))}
      </div>
    </div>
  );
};

CartRecommendations.propTypes = {
  closeCart: PropTypes.func,
};

export default CartRecommendations;
